import React, {ReactNode} from 'react'
import styled, {css} from 'styled-components'
import {currentMedia} from '../../../../styledComponents/ResponsiveConvenience'
import {Colors, FontSize} from '../../../../styledComponents/Constants'

interface Props {
    stepList: Step[]
}

export interface Step {
    label: string | ReactNode,
    isActive?: boolean,
    isSquare?: boolean,
}

export const NatResidentStepper: React.FC<Props> = props => {
    const {stepList} = props

    const activeIndex = stepList.findIndex(step => step.isActive)
    return (
        <div>
            <ListStep>
                {stepList.map((step, index) => (
                    <Stepper key={index}
                             isActive={step.isActive}
                             isBefore={activeIndex > index}
                             isSquare={step.isSquare}
                    >
                        <LabelWrapper isSquare={step.isSquare}>
                            <span>{step.label}</span>
                        </LabelWrapper>
                    </Stepper>
                ))}
            </ListStep>
        </div>
    )
}

const ListStep = styled.div`
  display: flex;
  justify-content: center;

  > div:first-child {
    margin-left: 0;
  }

  ${currentMedia.lessThan('sp')`
    flex-direction: column;
    > li:last-child {
      margin-bottom: 0;
    }
  `}
`

const Stepper = styled.div<{
    isActive?: boolean,
    isBefore?: boolean,
    isSquare?: boolean
}>`
  display: flex;
  align-items: center;
  background: ${Colors.Primary};
  color: ${Colors.Surface};
  border-color: ${Colors.Primary};
  position: relative;
  width: 100%;
  max-width: 155px;
  height: 56px;
  margin: 0 0 5px 25px;

  ${currentMedia.lessThan('sp')`
    margin: 0 auto 15px;
    max-width: 100%;
    height: 40px;
  `}

  ${props => !props.isSquare && ArrowStyle}
  ${props => props.isActive && ActiveStyle}
  ${props => props.isBefore && BeforeStyle}
`

const LabelWrapper = styled.div<{ isSquare?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 44px;
  font-size: ${FontSize.Middle};
  line-height: 1.1;
  text-align: center;
  border-color: inherit;
  border-width: ${props => props.isSquare ?
          `6px` : `6px 0 6px 6px`};
  border-style: solid;
  justify-content: center;

  ${currentMedia.lessThan('sp')`
    height: 28px;
    border-width: 4px;
    padding: 0 16px;
  `}
`

const ArrowStyle = css`
  &::after {
    border-left: 20px solid ${Colors.Primary};
    border-top: 28px solid transparent;
    border-bottom: 28px solid transparent;
    content: "";
    position: absolute;
    top: 0;
    left: 100%;
  }

  ${currentMedia.lessThan('sp')`
    margin: 0 auto 15px;
    width: 100%;
    height: 40px;

    &::after {
      left: 50%;
      border-top: 18px solid ${Colors.Primary};
      border-left: 15px solid transparent;
      border-right: 15px solid transparent;
      bottom: -40px;
      top: initial;
      margin-left: -16px;
      z-index: 5;
    }
  `}
`

const ActiveStyle = (props: { isSquare?: boolean }) => css`
  background-color: ${Colors.Surface};
  color: ${Colors.Primary};
  border-color: ${Colors.Primary};

  ${!props.isSquare && css`
    &::before {
      border-left: 11px solid ${Colors.Surface};
      border-top: 28px solid transparent;
      border-bottom: 28px solid transparent;
      content: "";
      position: absolute;
      left: 100%;
      top: 6px;
      z-index: 5;
      border-bottom-width: 23px;
      border-top-width: 22px;
    }

    ${currentMedia.lessThan('sp')`
      &::after {
        bottom: -42px;
      }
      &::before {
        border-top: 16px solid ${Colors.Surface};
        border-left: 13px solid transparent;
        border-right: 13px solid transparent;
        left: 50%;
        bottom: -31px;
        margin-left: -14px;
        top: initial;
        z-index: 6;
      }
    `}
  `}
`

const BeforeStyle = (props: { isSquare?: boolean }) => css`
  background-color: ${Colors.PrimaryComplete};
  color: ${Colors.Surface};
  border-color: ${Colors.PrimaryComplete};

  ${!props.isSquare && css`
    &::after {
      border-left: 20px solid ${Colors.PrimaryComplete};
    }

    ${currentMedia.lessThan('sp')`
      &::after {
        border-top: 14px solid ${Colors.PrimaryComplete};
        border-left: 15px solid transparent;;
      }
    `}
  `}
`